import type { AppConfig } from "../../types/appConfig";
import type {
  LeaveRequest,
  LeaveType,
  CreateLeaveRequest,
  UpdateLeaveRequest,
  LeaveRequestsQuery,
  UserLeaveTypeBalance,
} from "./Leave.typed";
import type { PaginatedResponse } from "../zekay/pagination";
import type { MultiComment } from "../zekay/comments";
import { assertResponseOk, expectStatuses, fetchZekay } from "../zekay/httpClient";

/** Echo/json: top-level PascalCase; nested Comment uses user/comment/date (go-zekay-models). */
function jsonBodyForLeaveRequestUpdate(update: UpdateLeaveRequest): string {
  const o: Record<string, unknown> = {};
  if (update.Status !== undefined) o.Status = update.Status;
  if (update.ReviewedBy !== undefined) o.ReviewedBy = update.ReviewedBy;
  if (update.MultiComments !== undefined) {
    o.MultiComments = update.MultiComments.map((c) => ({
      user: c.User,
      comment: c.Comment,
      date: c.Date,
    }));
  }
  return JSON.stringify(o);
}

export class LeaveModel {
  zekayAPI: string;

  constructor(config: AppConfig) {
    this.zekayAPI = config.zekayAPI;
  }

  async createLeaveRequest(request: CreateLeaveRequest): Promise<LeaveRequest> {
    const response = await fetchZekay(`${this.zekayAPI}/leave/requests`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request),
    });
    await expectStatuses(response, 201, 200);
    return (await response.json()) as LeaveRequest;
  }

  async getLeaveRequests(query?: LeaveRequestsQuery): Promise<PaginatedResponse<LeaveRequest>> {
    const params = new URLSearchParams();
    const page = query?.Page ?? 1;
    const limit = query?.Limit ?? 20;
    if (query) {
      if (query.UserID !== undefined) params.append("user_id", String(query.UserID));
      if (query.TypeID !== undefined) params.append("type_id", String(query.TypeID));
      if (query.Status !== undefined) params.append("status", String(query.Status));
      const fromQ = query.From ?? (query.StartDate ? query.StartDate.slice(0, 10) : undefined);
      const toQ = query.To ?? (query.EndDate ? query.EndDate.slice(0, 10) : undefined);
      if (fromQ) params.append("from", fromQ);
      if (toQ) params.append("to", toQ);
      if (query.Page !== undefined) params.append("page", String(query.Page));
      if (query.Limit !== undefined) params.append("limit", String(query.Limit));
      if (query.SortBy) params.append("sort_by", query.SortBy);
    }

    const url = `${this.zekayAPI}/leave/requests${params.toString() ? `?${params.toString()}` : ""}`;
    const response = await fetchZekay(url);
    await expectStatuses(response, 200, 204);
    if (response.status === 204) {
      return { data: [], total: 0, page, limit, total_pages: 0 };
    }
    const parsed = (await response.json()) as PaginatedResponse<LeaveRequest>;
    return {
      ...parsed,
      data: Array.isArray(parsed.data) ? parsed.data : [],
    };
  }

  async getLeaveRequest(id: number): Promise<LeaveRequest> {
    const response = await fetchZekay(`${this.zekayAPI}/leave/requests/${id}`);
    await assertResponseOk(response);
    return (await response.json()) as LeaveRequest;
  }

  async updateLeaveRequest(id: number, update: UpdateLeaveRequest): Promise<LeaveRequest> {
    const response = await fetchZekay(`${this.zekayAPI}/leave/requests/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: jsonBodyForLeaveRequestUpdate(update),
    });
    if (response.status === 204) {
      return this.getLeaveRequest(id);
    }
    await assertResponseOk(response);
    return (await response.json()) as LeaveRequest;
  }

  async deleteLeaveRequest(id: number): Promise<void> {
    const response = await fetchZekay(`${this.zekayAPI}/leave/requests/${id}`, {
      method: "DELETE",
    });
    await expectStatuses(response, 204);
  }

  async listLeaveTypes(): Promise<LeaveType[]> {
    const response = await fetchZekay(`${this.zekayAPI}/leave/types`);
    await expectStatuses(response, 200, 204);
    if (response.status === 204) return [];
    return (await response.json()) as LeaveType[];
  }

  async getLeaveType(id: number): Promise<LeaveType> {
    const response = await fetchZekay(`${this.zekayAPI}/leave/types/${id}`);
    await assertResponseOk(response);
    return (await response.json()) as LeaveType;
  }

  async createLeaveType(payload: Omit<LeaveType, "ID">): Promise<{ ID: number }> {
    const response = await fetchZekay(`${this.zekayAPI}/leave/types`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    await expectStatuses(response, 201);
    return (await response.json()) as { ID: number };
  }

  async updateLeaveType(id: number, payload: Omit<LeaveType, "ID">): Promise<void> {
    const response = await fetchZekay(`${this.zekayAPI}/leave/types/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    await expectStatuses(response, 204);
  }

  async deleteLeaveType(id: number): Promise<void> {
    const response = await fetchZekay(`${this.zekayAPI}/leave/types/${id}`, {
      method: "DELETE",
    });
    await expectStatuses(response, 204);
  }

  async listLeaveTypeBalances(
    typeId: number,
    page?: number,
    limit?: number,
  ): Promise<PaginatedResponse<UserLeaveTypeBalance>> {
    const params = new URLSearchParams();
    if (page !== undefined) params.append("page", String(page));
    if (limit !== undefined) params.append("limit", String(limit));

    const url = `${this.zekayAPI}/leave/types/${typeId}/balances${params.toString() ? `?${params.toString()}` : ""}`;
    const response = await fetchZekay(url);
    await expectStatuses(response, 200, 204);
    if (response.status === 204) {
      return { data: [], total: 0, page: 1, limit: limit || 20, total_pages: 0 };
    }
    const parsed = (await response
      .json()
      .catch(() => null)) as PaginatedResponse<UserLeaveTypeBalance> | null;
    const safe: PaginatedResponse<UserLeaveTypeBalance> = {
      data: [],
      total: 0,
      page: page || 1,
      limit: limit || 20,
      total_pages: 0,
    };
    if (!parsed || typeof parsed !== "object") return safe;
    return Array.isArray(parsed.data) ? parsed : { ...parsed, data: [] };
  }

  async listBalancesByUser(
    userId: number,
    page?: number,
    limit?: number,
  ): Promise<PaginatedResponse<UserLeaveTypeBalance>> {
    const params = new URLSearchParams();
    if (page !== undefined) params.append("page", String(page));
    if (limit !== undefined) params.append("limit", String(limit));
    const url = `${this.zekayAPI}/leave/users/${userId}/balances${params.toString() ? `?${params.toString()}` : ""}`;
    const response = await fetchZekay(url);
    await expectStatuses(response, 200, 204);
    if (response.status === 204) {
      return { data: [], total: 0, page: 1, limit: limit || 20, total_pages: 0 };
    }
    const parsed = (await response
      .json()
      .catch(() => null)) as PaginatedResponse<UserLeaveTypeBalance> | null;
    const safe: PaginatedResponse<UserLeaveTypeBalance> = {
      data: [],
      total: 0,
      page: page || 1,
      limit: limit || 20,
      total_pages: 0,
    };
    if (!parsed || typeof parsed !== "object") return safe;
    return Array.isArray(parsed.data) ? parsed : { ...parsed, data: [] };
  }

  async setLeaveTypeBalance(
    typeId: number,
    userId: number,
    payload: { Balance?: number; MultiComments?: MultiComment[] },
  ): Promise<void> {
    const response = await fetchZekay(`${this.zekayAPI}/leave/types/${typeId}/balances/${userId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    await expectStatuses(response, 204);
  }

  async deleteLeaveTypeBalance(typeId: number, userId: number): Promise<void> {
    const response = await fetchZekay(`${this.zekayAPI}/leave/types/${typeId}/balances/${userId}`, {
      method: "DELETE",
    });
    await expectStatuses(response, 204, 200);
  }

  async incrementLeaveTypeBalance(
    typeId: number,
    userId: number,
    payload: { Delta: number },
  ): Promise<void> {
    const response = await fetchZekay(
      `${this.zekayAPI}/leave/types/${typeId}/balances/${userId}/increment`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
    );
    await expectStatuses(response, 204);
  }
}
